import type { HelpContent } from './types';

export const settingsTemplatesHelp: HelpContent = {
  pageTitle: 'Invoice & Email Templates',
  description: 'Invoice & Email Templates is where administrators create and manage the layouts used to generate client invoices and the automated email templates sent for billing events.',

  functionality: {
    overview:
      'This page provides a template editor for invoice layouts and email communications. Templates can include dynamic fields (merge tags) that are populated automatically from project and client data when an invoice or email is generated.',
    keyFeatures: [
      {
        label: 'Invoice layout editor',
        detail: 'Build and customise the visual layout of PDF invoices sent to clients.',
      },
      {
        label: 'Email template editor',
        detail: 'Create the content for automated emails triggered by billing events such as invoice creation or payment reminders.',
      },
      {
        label: 'Merge fields',
        detail: 'Insert dynamic placeholders that are replaced with real data (e.g. client name, invoice number, due date) when a document is generated.',
      },
      {
        label: 'Preview',
        detail: 'See a rendered preview of how the template will look with sample data before activating it.',
      },
      {
        label: 'Multiple templates',
        detail: 'Create different templates for different client types or billing scenarios and select which template to use when generating a document.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Template list',
        description: 'Lists all saved invoice and email templates with their name, type, and last-modified date.',
      },
      {
        name: 'Create Template button',
        description: 'Opens the template creation form to build a new invoice or email template.',
      },
      {
        name: 'Template editor',
        description: 'A rich text editor where you compose the template content and insert merge fields.',
      },
      {
        name: 'Merge fields panel',
        description: 'A sidebar listing all available dynamic fields you can insert into the template (e.g. {{client.name}}, {{invoice.number}}).',
      },
      {
        name: 'Preview button',
        description: 'Renders the template with sample data so you can check the output before saving.',
      },
    ],
    useCases: [
      'An administrator creates a new invoice template that includes the company logo, payment terms, and bank details.',
      'A finance lead creates an overdue payment reminder email template that automatically inserts the client name and outstanding amount.',
      'An administrator duplicates an existing template and adjusts it for a specific high-value client who requires a different invoice format.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Before creating templates, ensure your company logo and bank details are set in General Settings — these are used as merge fields in invoice templates.',
    steps: [
      {
        title: 'How do I create a new template?',
        items: [
          { text: 'Click Create Template.' },
          { text: 'Select the template type: Invoice or Email.' },
          { text: 'Enter a template name.' },
          { text: 'Use the editor to compose the template content.' },
          {
            text: 'Click on a merge field in the sidebar to insert it at the cursor position.',
            tip: 'Merge fields follow the format {{category.fieldName}}. Type {{ in the editor to see an autocomplete list.',
          },
          { text: 'Click Preview to check how the template will render.' },
          { text: 'Click Save to store the template.' },
        ],
      },
      {
        title: 'How do I edit an existing template?',
        items: [
          { text: 'Find the template in the list.' },
          { text: 'Click the edit icon next to it.' },
          { text: 'Make your changes in the editor.' },
          {
            text: 'Click Preview to review the output.',
            note: 'Editing a template does not affect documents that have already been generated using the previous version.',
          },
          { text: 'Click Save.' },
        ],
      },
    ],
    proTips: [
      'Always preview a template with sample data before activating it — merge fields that reference missing data will render as blank, which can look unprofessional on client-facing documents.',
      'Name templates descriptively (e.g. "Standard Invoice – UK Clients") so users can identify the correct one quickly when generating documents.',
      'Keep email templates concise — clients are more likely to act on short, direct emails than on long formatted messages.',
    ],
  },

  faq: [
    {
      question: 'Can I have more than one invoice template?',
      answer:
        'Yes. You can create multiple templates and select which one to use when generating an invoice. This is useful for different client types or billing formats.',
    },
    {
      question: 'What are merge fields and how do I use them?',
      answer:
        'Merge fields are placeholders like {{client.name}} or {{invoice.totalAmount}} that are replaced with real data when a document is generated. Insert them from the merge fields sidebar or by typing {{ in the editor.',
    },
    {
      question: 'Does editing a template change invoices that have already been sent?',
      answer:
        'No. Editing a template only affects documents generated after the change is saved. Previously generated invoices retain the layout that was active when they were created.',
    },
    {
      question: 'Can I delete a template that is set as the default?',
      answer:
        'You must assign a different template as the default before deleting the current default. The system will warn you if you try to delete the active default template.',
    },
  ],

  relatedPages: [
    { label: 'General Settings', path: '/settings/general' },
    { label: 'Notifications', path: '/settings/notifications' },
    { label: 'Settings Hub', path: '/settings' },
  ],
};
